"use client";

import { c as _c } from "react/compiler-runtime";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "@components/ui/styled-badge";
import { Button } from "@components/ui/styled-button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@components/ui/tooltip";
import { MoreHorizontal } from "lucide-react";
import { usePathname } from "next/navigation";
import { countChangedFields } from "./utils";
export interface ToolbarAction {
  label: string;
  /** Return `false` to keep the toolbar open (e.g. after a no-op). */
  onClick: () => Promise<void | boolean>;
  /** Fired when the user clicks a disabled action (wrapper catches the click). */
  onDisabledClick?: () => void;
  icon?: React.ReactNode;
  danger?: boolean;
  disabled?: boolean;
  color?: "primary" | "danger";
  tooltip?: string;
}
interface ToolbarContextType {
  setToolbar: (options: {
    actions: ToolbarAction[];
    message?: string;
  }) => void;
}
const ToolbarContext = createContext<ToolbarContextType | undefined>(undefined);
export const ToolbarProvider: React.FC<{
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(20);
  const {
    children
  } = t0;
  const pathname = usePathname();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      actions: []
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [toolbar, setToolbarState] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      actions: []
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [displayToolbar, setDisplayToolbar] = useState(t2);
  const [isToolbarMounted, setIsToolbarMounted] = useState(false);
  const [isToolbarClosing, setIsToolbarClosing] = useState(false);
  const hideToolbarTimerRef = useRef(null);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = options => {
      setToolbarState(prev => {
        const next = {
          actions: options.actions,
          message: options.message
        };
        if (prev.actions.length === 0 && next.actions.length === 0 && prev.message === next.message) {
          return prev;
        }
        return next;
      });
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const setToolbar = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setToolbarState({
        actions: []
      });
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== pathname) {
    t5 = [pathname];
    $[4] = pathname;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[6] !== isToolbarMounted || $[7] !== toolbar) {
    t6 = () => {
      const hasActions = toolbar.actions.length > 0;
      if (hasActions) {
        if (hideToolbarTimerRef.current) {
          clearTimeout(hideToolbarTimerRef.current);
          hideToolbarTimerRef.current = null;
        }
        setDisplayToolbar(toolbar);
        setIsToolbarMounted(true);
        setIsToolbarClosing(false);
        return;
      }
      if (isToolbarMounted) {
        setIsToolbarClosing(true);
        hideToolbarTimerRef.current = setTimeout(() => {
          setIsToolbarMounted(false);
          setIsToolbarClosing(false);
          setDisplayToolbar({
            actions: []
          });
        }, 180);
      }
    };
    t7 = [180, isToolbarMounted, toolbar];
    $[6] = isToolbarMounted;
    $[7] = toolbar;
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => () => {
      if (hideToolbarTimerRef.current) {
        clearTimeout(hideToolbarTimerRef.current);
      }
    };
    t9 = [];
    $[10] = t8;
    $[11] = t9;
  } else {
    t8 = $[10];
    t9 = $[11];
  }
  useEffect(t8, t9);
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      setToolbar
    };
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const contextValue = t10;
  let t11;
  if ($[13] !== displayToolbar || $[14] !== isToolbarClosing || $[15] !== isToolbarMounted) {
    t11 = isToolbarMounted && <Toolbar actions={displayToolbar.actions} message={displayToolbar.message} setToolbar={setToolbar} isClosing={isToolbarClosing} />;
    $[13] = displayToolbar;
    $[14] = isToolbarClosing;
    $[15] = isToolbarMounted;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== children || $[18] !== t11) {
    t12 = <ToolbarContext.Provider value={contextValue}>{children}{t11}</ToolbarContext.Provider>;
    $[17] = children;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  return t12;
};
export const useToolbar = () => {
  const context = useContext(ToolbarContext);
  if (!context) {
    throw new Error("useToolbar must be used within a ToolbarProvider");
  }
  return context;
};
const Toolbar: React.FC<{
  actions: ToolbarAction[];
  message?: string;
  setToolbar: ToolbarContextType["setToolbar"];
  isClosing: boolean;
}> = ({
  actions,
  message,
  setToolbar,
  isClosing
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const getActionColor = useCallback((action: ToolbarAction): "primary" | "danger" => {
    if (action.color === "danger") return "danger";
    return "primary";
  }, []);
  const executeAction = useCallback(async (action_0: ToolbarAction) => {
    const isCancel = action_0.label === "Cancel";
    let keepToolbar = false;
    try {
      if (!isCancel) setIsLoading(true);
      const result = await action_0.onClick();
      keepToolbar = result === false;
    } catch (err) {
      console.error(err);
    } finally {
      if (!isCancel) setIsLoading(false);
      if (!keepToolbar) setToolbar({
        actions: []
      });
    }
  }, [setToolbar]);
  return <div className={`fixed bottom-6 left-1/2 z-[9999] flex w-auto -translate-x-1/2 items-center justify-between rounded-lg bg-primary px-3 py-1 text-white shadow-[0_8px_20px_rgba(0,0,0,0.3)] backdrop-blur-[10px] transition-all duration-200 ${isClosing ? "toolbar-exit pointer-events-none" : "toolbar-enter"}`}>
      <Badge variant="none" className="mr-4 whitespace-nowrap border-0 bg-transparent px-2 py-1 text-center text-xs font-semibold text-white">
        {message}
      </Badge>
      <div className="flex w-full items-center justify-end p-0">
        {/* Desktop view - show all buttons with dividers */}
        <TooltipProvider>
          <div className="hidden items-center justify-end md:flex">
          {actions.map((action_1, index) => {
            const looksDisabled = Boolean(action_1.disabled) || isLoading;
            const button = <Button typeColor={getActionColor(action_1)} variant="filled" disabled={false} aria-disabled={looksDisabled || undefined} icon={action_1.icon} onClick={() => {
              // Keep clickable when "disabled" so onClick can toast; block only while loading.
              if (isLoading) return;
              void executeAction(action_1);
            }} loading={isLoading && !action_1.disabled} buttonLabel={action_1.label} className={action_1.tooltip ? `px-3 text-xs${looksDisabled ? " cursor-not-allowed opacity-50" : ""}` : `w-full border-0 px-3 text-xs font-semibold${looksDisabled ? " cursor-not-allowed opacity-50" : ""}`}>
                {action_1.label}
              </Button>;
            return <div key={index} className="flex items-center">
              {index > 0 && <div className="mx-2 h-5 w-px bg-white/60" />}
              {action_1.tooltip ? <Tooltip>
                  <TooltipTrigger asChild>
                    <span className={looksDisabled ? "cursor-not-allowed" : undefined}>
                      {button}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{action_1.tooltip}</TooltipContent>
                </Tooltip> : button}
            </div>;
          })}
          </div>
        </TooltipProvider>

        {/* Mobile view - show dots menu for all actions */}
        <div className="md:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
            <Button variant="text" typeColor="primary" icon={<MoreHorizontal className="h-4 w-4" />} iconOnly className="text-white" loading={isLoading}>
                <MoreHorizontal className="h-4 w-4" />
            </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {actions.map((action_2, index_0) => <DropdownMenuItem key={index_0} disabled={isLoading} onSelect={e => {
              e.preventDefault();
              if (isLoading) return;
              void executeAction(action_2);
            }} className={`text-xs ${action_2.color === "danger" || action_2.disabled ? "text-danger" : ""}`}>
                  <div className="mr-2">{action_2.icon}</div>
                  <span>{action_2.label || action_2.tooltip}</span>
                </DropdownMenuItem>)}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>;
};
export const ToolbarHooker = t0 => {
  const $ = _c(11);
  const {
    setShowToolbar,
    setFieldChange,
    defaultValues,
    values,
    setFormikErrors,
    errors
  } = t0;
  let t1;
  if ($[0] !== defaultValues || $[1] !== errors || $[2] !== setFieldChange || $[3] !== setFormikErrors || $[4] !== setShowToolbar || $[5] !== values) {
    t1 = () => {
      const count = countChangedFields(defaultValues, values);
      if (count > 0) {
        setShowToolbar(true);
        setFieldChange(count);
      }
      if (Object.keys(errors || {}).length >= 0) {
        setFormikErrors && setFormikErrors(errors);
      }
      if (count === 0) {
        setShowToolbar(false);
      }
    };
    $[0] = defaultValues;
    $[1] = errors;
    $[2] = setFieldChange;
    $[3] = setFormikErrors;
    $[4] = setShowToolbar;
    $[5] = values;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== errors || $[8] !== values) {
    t2 = [values, errors];
    $[7] = errors;
    $[8] = values;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  useEffect(t1, t2);
  let t3;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <></>;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};